import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  BellRing,
  Bookmark,
  Camera,
  Car,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Globe,
  Home,
  History,
  Info,
  Printer,
  RefreshCw,
  ShoppingCart,
  Truck,
  Users,
  X,
} from 'lucide-react';

import ProductImageView, { ProductImageThumb, resolveProductImageUrl } from './ProductImageView';
import ProductEditDialog from './ProductEditDialog';
import ProductMovementHistoryModal from './ProductMovementHistoryModal';
import { cn } from '@/lib/utils';

function formatPrice(value) {
  const rounded = Math.round(value || 0);
  return `${rounded.toLocaleString('ru-RU')} ₸`;
}

function formatCurrency(value, currency) {
  const rounded = Math.round(value || 0);
  return `${rounded.toLocaleString('ru-RU')} ${currency || 'KZT'}`;
}

function resolveImageUrl(image) {
  return resolveProductImageUrl(image);
}

function SpecCell({ label, value, mono = false, className }) {
  return (
    <div className={cn('px-4 py-3 border-b border-r border-gray-100', className)}>
      <div className="text-[11px] text-gray-400 mb-0.5">{label}</div>
      <div className={cn('text-sm text-gray-900', mono && 'font-mono text-xs')}>
        {value || '—'}
      </div>
    </div>
  );
}

function ImageLightbox({
  images,
  activeIndex,
  onIndexChange,
  imageVersion,
  alt,
  onClose,
}) {
  const image = images[activeIndex];
  const hasMultiple = images.length > 1;

  const goPrev = () => {
    onIndexChange(activeIndex === 0 ? images.length - 1 : activeIndex - 1);
  };

  const goNext = () => {
    onIndexChange(activeIndex === images.length - 1 ? 0 : activeIndex + 1);
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }
      if (!hasMultiple) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        e.stopPropagation();
        onIndexChange(activeIndex === 0 ? images.length - 1 : activeIndex - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        e.stopPropagation();
        onIndexChange(activeIndex === images.length - 1 ? 0 : activeIndex + 1);
      }
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [activeIndex, hasMultiple, images.length, onClose, onIndexChange]);

  if (!image) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[300] bg-black/95 flex items-center justify-center p-4 md:p-12"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Просмотр изображения"
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-4 right-4 rounded-full bg-white/10 hover:bg-white/20 text-white p-2 transition-colors z-10"
        aria-label="Закрыть"
      >
        <X className="w-6 h-6" />
      </button>

      {hasMultiple && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 hover:bg-white/20 text-white p-3 transition-colors z-10"
            aria-label="Предыдущее фото"
          >
            <ChevronLeft className="w-7 h-7" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 hover:bg-white/20 text-white p-3 transition-colors z-10"
            aria-label="Следующее фото"
          >
            <ChevronRight className="w-7 h-7" />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm bg-black/40 px-3 py-1 rounded-full z-10">
            {activeIndex + 1} / {images.length}
          </div>
        </>
      )}

      <ProductImageView
        image={image}
        alt={`${alt} ${activeIndex + 1}`}
        imageVersion={imageVersion}
        className="max-w-none max-h-none w-auto h-auto object-contain"
        style={{ maxWidth: 'min(100vw - 6rem, 100%)', maxHeight: 'min(100vh - 6rem, 100%)' }}
        onClick={(e) => e.stopPropagation()}
      />
    </div>,
    document.body
  );
}

export default function ProductDetailModal({
  product,
  onClose,
  imageLoading,
  imageProgress,
  onRefreshImages,
  onSearchImage,
  isAdmin,
  linkedOffers,
  linkedBase,
  linkedLoading,
  imageRetryRef,
  onAddToCart,
  onToggleWishList,
  onNotifyClient,
  onProductUpdated,
  user,
  token,
}) {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [offersExpanded, setOffersExpanded] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [wishListActive, setWishListActive] = useState(false);
  const [wishListToggling, setWishListToggling] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [imageVersion, setImageVersion] = useState(0);

  const imagesKey = (product?.images || []).map((img) => img.filename || img.url || '').join('|');

  useEffect(() => {
    setWishListActive(!!product?.wish_list);
  }, [product?.id, product?.wish_list]);

  useEffect(() => {
    if (!imageLoading) {
      setImageVersion((v) => v + 1);
    }
  }, [imageLoading, imagesKey]);

  if (!product) return null;

  const images = product.images || [];
  const activeImage = images[activeImageIndex] || {};
  const activeSrc = activeImage.url || activeImage.source_url ? resolveImageUrl(activeImage) : null;
  const barcode = Array.isArray(product.barcodes) && product.barcodes.length > 0
    ? product.barcodes[0]
    : null;
  const inStock = product.in_stock > 0;

  const handleOpenChange = (open) => {
    if (!open) {
      if (lightboxOpen) {
        setLightboxOpen(false);
        return;
      }
      if (editOpen) {
        setEditOpen(false);
        return;
      }
      if (historyOpen) {
        setHistoryOpen(false);
        return;
      }
      setActiveImageIndex(0);
      setOffersExpanded(false);
      setEditOpen(false);
      setHistoryOpen(false);
      onClose();
    }
  };

  const handleAddToCart = async () => {
    if (!onAddToCart || !user) return;
    setAddingToCart(true);
    try {
      await onAddToCart(product.id, product.packing_rate || 1);
    } finally {
      setAddingToCart(false);
    }
  };

  const handleToggleWishList = async () => {
    if (!onToggleWishList || !user || wishListToggling) return;
    const previous = wishListActive;
    setWishListActive(!previous);
    setWishListToggling(true);
    try {
      await onToggleWishList(product.id, previous);
    } catch {
      setWishListActive(previous);
    } finally {
      setWishListToggling(false);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank', 'noopener,noreferrer');
    if (!printWindow) return;

    const specs = [
      ['Код', product.code],
      ['OEM', product.oem],
      ['Артикул', product.articul],
      ['Бренд', product.brand],
      ['Упаковка', product.packing_rate || 1],
      ['Штрихкод', barcode],
      ['NTIN', product.ntin],
      ['Цена', formatPrice(product.client_price)],
      ['Наличие', inStock ? `${product.in_stock} ${product.unit}` : 'Нет'],
    ]
      .filter(([, value]) => value != null && value !== '')
      .map(
        ([label, value]) =>
          `<tr><td style="padding:6px 12px;color:#6b7280;width:140px">${label}</td><td style="padding:6px 12px;font-weight:600">${value}</td></tr>`
      )
      .join('');

    printWindow.document.write(`<!DOCTYPE html>
<html lang="ru">
<head>
  <meta charset="utf-8" />
  <title>${product.name}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; margin: 24px; color: #111827; }
    h1 { font-size: 20px; margin: 0 0 8px; }
    .meta { color: #6b7280; font-size: 13px; margin-bottom: 20px; }
    img { max-width: 320px; max-height: 320px; object-fit: contain; border: 1px solid #e5e7eb; border-radius: 8px; }
    table { border-collapse: collapse; width: 100%; max-width: 480px; margin-top: 16px; }
    td { border-bottom: 1px solid #f3f4f6; font-size: 14px; }
  </style>
</head>
<body>
  <h1>${product.name}</h1>
  <div class="meta">${[product.brand, product.category].filter(Boolean).join(' · ')}</div>
  ${activeSrc ? `<img src="${activeSrc}" alt="${product.name}" />` : ''}
  <table>${specs}</table>
</body>
</html>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return (
    <>
      <Dialog open={!!product} onOpenChange={handleOpenChange}>
        <DialogContent
          className="max-w-5xl p-0 gap-0 overflow-hidden"
          onEscapeKeyDown={(e) => {
            if (lightboxOpen) {
              e.preventDefault();
              setLightboxOpen(false);
            }
          }}
        >
          <div className="px-6 pt-5 pb-3 border-b border-gray-100">
            <Breadcrumb>
              <BreadcrumbList className="text-xs text-gray-400">
                <BreadcrumbItem>
                  <BreadcrumbLink className="flex items-center gap-1 cursor-default">
                    <Home className="w-3.5 h-3.5" />
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink className="cursor-default">Каталог</BreadcrumbLink>
                </BreadcrumbItem>
                {product.category && (
                  <>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbPage className="text-gray-600 font-normal">
                        {product.category}
                      </BreadcrumbPage>
                    </BreadcrumbItem>
                  </>
                )}
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-0">
            <div className="p-6 md:pr-4 border-b md:border-b-0 md:border-r border-gray-100">
              <div className="relative bg-gray-50 border border-gray-200 rounded-xl flex items-center justify-center min-h-[280px] overflow-hidden">
                {imageLoading ? (
                  <div className="flex flex-col items-center gap-3 py-16">
                    <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                    <div className="text-sm text-gray-600">Загрузка фото… {imageProgress}%</div>
                  </div>
                ) : activeSrc ? (
                  <>
                    <ProductImageView
                      image={activeImage}
                      alt={product.name}
                      imageVersion={imageVersion}
                      onClick={() => setLightboxOpen(true)}
                      onError={() => {
                        if (imageRetryRef.current.has(product.id)) return;
                        imageRetryRef.current.add(product.id);
                        onRefreshImages(product.id, { force: true, suppressToast: true });
                      }}
                      className="max-h-[320px] max-w-full object-contain p-4 cursor-zoom-in"
                      title="Нажмите для просмотра в полном размере"
                    />
                    {images.length > 1 && (
                      <>
                        <button
                          type="button"
                          className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-1.5 shadow border border-gray-200"
                          onClick={() =>
                            setActiveImageIndex((prev) =>
                              prev === 0 ? images.length - 1 : prev - 1
                            )
                          }
                        >
                          <ChevronLeft className="w-5 h-5 text-gray-700" />
                        </button>
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-1.5 shadow border border-gray-200"
                          onClick={() =>
                            setActiveImageIndex((prev) =>
                              prev === images.length - 1 ? 0 : prev + 1
                            )
                          }
                        >
                          <ChevronRight className="w-5 h-5 text-gray-700" />
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <div className="text-center space-y-3 px-6 py-12">
                    <Camera className="w-10 h-10 text-gray-300 mx-auto" />
                    <p className="text-sm text-gray-500">
                      {product.image_unavailable
                        ? 'Изображение недоступно'
                        : 'Изображения недоступны'}
                    </p>
                  </div>
                )}
              </div>

              {images.length > 0 && (
                <div className="mt-3">
                  <div className="flex gap-2 flex-wrap">
                    {images.map((img, idx) => (
                      <ProductImageThumb
                        key={`${img.filename || img.url || idx}-${imageVersion}`}
                        image={img}
                        alt={`${product.name} ${idx + 1}`}
                        selected={idx === activeImageIndex}
                        onSelect={() => setActiveImageIndex(idx)}
                        imageVersion={imageVersion}
                      />
                    ))}
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1.5">
                    {images.length} {images.length === 1 ? 'фото' : 'фото'}
                  </p>
                </div>
              )}

              {isAdmin && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onRefreshImages(product.id, { force: true })}
                    disabled={imageLoading}
                  >
                    <RefreshCw className={cn('w-4 h-4', imageLoading && 'animate-spin')} />
                    {imageLoading ? 'Загрузка…' : 'Обновить с сайта'}
                  </Button>
                  {onSearchImage && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onSearchImage(product)}
                      disabled={imageLoading}
                    >
                      <Globe className="w-4 h-4" />
                      Найти в интернете
                    </Button>
                  )}
                </div>
              )}

              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  onClick={handlePrint}
                >
                  <Printer className="w-4 h-4" />
                  Печать
                </Button>
                {user && token && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-slate-700 border-slate-200 hover:bg-slate-50"
                    onClick={() => setHistoryOpen(true)}
                  >
                    <History className="w-4 h-4" />
                    История
                  </Button>
                )}
              </div>
            </div>

            <div className="p-6 md:pl-4 flex flex-col min-h-0">
              <h2 className="text-xl font-bold text-gray-900 leading-snug pr-8">
                {product.name}
              </h2>

              <div className="mt-2 flex flex-wrap items-center gap-2">
                {product.category && (
                  <Badge
                    variant="secondary"
                    className="w-fit gap-1.5 bg-gray-100 text-gray-600 font-normal border-0"
                  >
                    <Car className="w-3.5 h-3.5" />
                    {product.category}
                  </Badge>
                )}
                {wishListActive && (
                  <span className="text-[10px] font-semibold text-yellow-800 bg-yellow-100 px-2 py-0.5 rounded-full">
                    Ожидается
                  </span>
                )}
              </div>

              <div className="mt-4 border border-gray-200 rounded-xl overflow-hidden grid grid-cols-2 [&>*:nth-child(2n)]:border-r-0">
                <SpecCell label="Код" value={product.code} mono />
                <SpecCell label="Упаковка" value={product.packing_rate || 1} />
                <SpecCell label="OEM" value={product.oem} mono />
                <SpecCell label="Штрихкод" value={barcode} mono />
                <SpecCell label="Артикул" value={product.articul} mono />
                <SpecCell label="NTIN" value={product.ntin} mono className="border-b-0" />
                <SpecCell label="Бренд" value={product.brand} className="border-b-0" />
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-3">
                <div className="text-3xl font-bold text-gray-900 tracking-tight">
                  {formatPrice(product.client_price)}
                </div>
                {onAddToCart && (
                  <Button
                    className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm px-5"
                    onClick={handleAddToCart}
                    disabled={!user || addingToCart}
                  >
                    <ShoppingCart className="w-4 h-4" />
                    {addingToCart ? 'Добавляем…' : 'Добавить в корзину'}
                  </Button>
                )}
              </div>

              <div className="mt-3 flex items-center gap-3">
                {inStock ? (
                  <span className="inline-flex items-center gap-1.5 text-sm text-green-700">
                    <Check className="w-4 h-4" />
                    В наличии · {product.in_stock} {product.unit}
                  </span>
                ) : product.in_transit ? (
                  <span className="inline-flex items-center gap-1.5 text-sm text-blue-700">
                    <Truck className="w-4 h-4" />
                    В пути на склад
                  </span>
                ) : (
                  <span className="text-sm text-gray-500">Нет в наличии</span>
                )}
                {onToggleWishList && (
                  <Button
                    variant="outline"
                    size="icon"
                    className={cn(
                      'h-9 w-9 shrink-0 border-gray-200 transition-colors',
                      wishListActive && 'text-yellow-700 border-yellow-300 bg-yellow-50 hover:bg-yellow-100'
                    )}
                    onClick={handleToggleWishList}
                    disabled={!user || wishListToggling}
                    title={wishListActive ? 'Убрать из ожидаемых (сотрудники)' : 'В ожидаемые (сотрудники)'}
                  >
                    <Bookmark className={cn('w-4 h-4', wishListActive && 'fill-current')} />
                  </Button>
                )}
                {onNotifyClient && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 shrink-0 border-amber-200 text-amber-800 bg-amber-50 hover:bg-amber-100"
                    onClick={() => onNotifyClient(product)}
                    disabled={!user}
                    title="Сообщить клиенту в WhatsApp, когда появится"
                  >
                    <BellRing className="w-4 h-4" />
                    Клиенту
                  </Button>
                )}
              </div>

              {product.description && (
                <p className="mt-4 text-sm text-gray-600 whitespace-pre-line leading-relaxed">
                  {product.description}
                </p>
              )}

              <div className="mt-5">
                <button
                  type="button"
                  className="flex items-center gap-2 text-sm font-medium text-gray-700 w-full"
                  onClick={() => setOffersExpanded((v) => !v)}
                >
                  <Users className="w-4 h-4 text-gray-400" />
                  Предложения поставщиков
                  <ChevronDown
                    className={cn(
                      'w-4 h-4 text-gray-400 ml-auto transition-transform',
                      offersExpanded && 'rotate-180'
                    )}
                  />
                </button>

                {offersExpanded && (
                  <div className="mt-2 space-y-2">
                    {linkedLoading ? (
                      <div className="text-sm text-gray-500 py-2">Подбираем совпадения…</div>
                    ) : linkedOffers && linkedOffers.length > 0 ? (
                      <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
                        {linkedOffers.map((offer) => (
                          <div
                            key={offer.id}
                            className="flex items-center justify-between rounded-xl border border-gray-200 px-4 py-3 bg-white hover:bg-gray-50/80 transition-colors"
                          >
                            <div className="min-w-0">
                              <div className="text-xs text-gray-400">{offer.supplier_name}</div>
                              <div className="font-semibold text-sm text-gray-900 truncate">
                                {offer.name}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {[offer.brand, offer.articul || offer.oem || offer.code]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </div>
                            </div>
                            <div className="text-right ml-4 shrink-0">
                              <div className="font-bold text-gray-900">
                                {formatCurrency(offer.client_price, offer.supplier_currency || 'KZT')}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {offer.in_stock > 0
                                  ? `${offer.in_stock} ${offer.unit || 'шт'}`
                                  : 'Нет в наличии'}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500 py-2">
                        Пока нет связанных предложений
                      </div>
                    )}
                  </div>
                )}
              </div>

              {linkedBase?.name && (
                <div className="mt-4 flex items-start gap-2 rounded-lg bg-gray-50 border border-gray-100 px-3 py-2.5 text-xs text-gray-500">
                  <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400" />
                  <span>
                    Базовая номенклатура: {linkedBase.name} ({linkedBase.code || '—'})
                  </span>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 sm:justify-end gap-2">
            <Button variant="outline" className="border-blue-200 text-blue-600 hover:bg-blue-50" onClick={onClose}>
              Закрыть
            </Button>
            {isAdmin && (
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => setEditOpen(true)}
              >
                Редактировать
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {lightboxOpen && images.length > 0 && (
        <ImageLightbox
          images={images}
          activeIndex={activeImageIndex}
          onIndexChange={setActiveImageIndex}
          imageVersion={imageVersion}
          alt={product.name}
          onClose={() => setLightboxOpen(false)}
        />
      )}

      {isAdmin && (
        <ProductEditDialog
          product={product}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          token={token}
          onSaved={(updated) => onProductUpdated?.(updated)}
        />
      )}

      {user && token && (
        <ProductMovementHistoryModal
          product={product}
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          token={token}
          user={user}
        />
      )}
    </>
  );
}
